import HttpBackend from "i18next-http-backend";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { supportedLanguageCodes } from "./supportedLanguages";
import ChainedBackend from "i18next-chained-backend";
import CustomerTranslations from "../utils/CustomerTranslations";

const getLocalisationURL = (): string => {
  const runtimeEnv = process.env.RUNTIME_ENV ?? "development";

  let uri = "http://localhost:8000/locales";

  // These code paths can never be hit by tests
  /* istanbul ignore next */
  if (runtimeEnv === "production") {
    uri = "https://localisation.appointedd.com";
  } else if (runtimeEnv === "t2") {
    uri = "https://localisation-t2.appointedd.com";
  } else if (runtimeEnv === "t1") {
    uri = "https://localisation-t1.appointedd.com";
  }

  return uri;
};

const backendOptions = [
  { loadPath: `${getLocalisationURL()}/booking-tools/{{lng}}/{{ns}}.json` },
];
const backends = [HttpBackend];

if (CustomerTranslations.isCustomerTranslationEnabled()) {
  // TODO: This is currently just a placeholder endpoint for customer translations allowing us to simulate using
  // a JSON file in an S3 bucket, in future this will be wired up to an API that generates the appropriate translations.
  backendOptions.push({
    loadPath: `https://appointedd-assets.s3.eu-west-1.amazonaws.com/localisation-testing/{{lng}}/{{ns}}.json`,
  });
  backends.push(HttpBackend);
}

const ns = ["common"];
const customerNamespace = CustomerTranslations.getNamespace();
if (customerNamespace && CustomerTranslations.isCustomerTranslationEnabled()) {
  ns.push(customerNamespace);
}

const chainedBackendOptions = {
  backendOptions,
  backends,
};

const chainedBackend = new ChainedBackend(null, chainedBackendOptions);

void i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(chainedBackend)
  .init({
    debug: process.env.NODE_ENV === "development",
    defaultNS: "common",
    detection: {
      caches: [],
      lookupQuerystring: "locale",
      convertDetectedLanguage: (lng: string) => {
        if (lng === "en") {
          return "en-GB";
        }
        if (lng === "no" || lng === "nn") {
          // Our Norwegian translations default to Bokmål, for some reason
          // so we need to ensure this is always selected for norwegian detection
          return "nb";
        }
        return lng;
      },
    },
    fallbackLng: ["en-US", "en-GB"],
    interpolation: {
      escapeValue: false,
    },
    load: "all",
    nonExplicitSupportedLngs: true,
    ns,
    supportedLngs: [...supportedLanguageCodes, "en", "cimode"],
  });

/**
 * Convenience function to set the translation engine to display keys only.
 * Useful for tests.
 */
export async function setTranslationTestMode(): Promise<void> {
  await i18n.changeLanguage("cimode");
}

export default i18n;
